import {
  ContractManagementDateType,
  type CalendarEvent,
  type ContractManagementDateCreate,
  type ContractManagementDateInDb,
  type ContractManagementDateUpdate,
} from '@/api';

export const DATE_TYPE_LABELS: Record<string, string> = {
  [ContractManagementDateType.EFFECTIVE_FROM]: 'Effective from',
  [ContractManagementDateType.EFFECTIVE_TO]: 'Effective to',
};

export const RECURRENCE_TYPE_LABELS: Record<string, string> = {
  never: 'Does not repeat',
  until_date: 'Until a date',
  count: 'A number of times',
  infinite: 'Forever',
};

export const RECURRENCE_FREQUENCY_LABELS: Record<string, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year',
};

/** The `recurrence_*` keys a date carries, shared with an extracted calendar event. */
export type DateRecurrence = Pick<
  ContractManagementDateInDb,
  | 'recurrence_type'
  | 'recurrence_count'
  | 'recurrence_until_date'
  | 'recurrence_frequency'
  | 'recurrence_interval'
>;

type RecurrenceFields = {
  [K in keyof DateRecurrence]-?: DateRecurrence[K] | null;
};

/**
 * A date as its form reads it: every recurrence part present, so each is a
 * form field of its own.
 */
export type DateRow<T extends ContractManagementDateInDb = ContractManagementDateInDb> = T &
  RecurrenceFields;

export function toDateRow<T extends ContractManagementDateInDb>(date: T): DateRow<T> {
  return {
    ...date,
    recurrence_count: date.recurrence_count ?? null,
    recurrence_until_date: date.recurrence_until_date ?? null,
    recurrence_frequency: date.recurrence_frequency ?? null,
    recurrence_interval: date.recurrence_interval ?? null,
  };
}

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const number = (value: unknown): number | null => {
  const parsed = Number.parseInt(text(value), 10);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * A date's form values (all strings) as an API body. Parts the chosen
 * recurrence type doesn't use are sent as null, so switching type clears them.
 */
export function dateBody(
  values: Record<string, unknown>
): Omit<ContractManagementDateCreate, 'type'> & ContractManagementDateUpdate {
  const recurrenceType = (text(values.recurrence_type) ||
    'never') as DateRecurrence['recurrence_type'];
  const repeats = recurrenceType !== 'never';
  return {
    date: text(values.date) || null,
    description: text(values.description) || null,
    recurrence_type: recurrenceType,
    recurrence_frequency: repeats
      ? ((text(values.recurrence_frequency) || null) as DateRecurrence['recurrence_frequency'])
      : null,
    recurrence_interval: repeats ? number(values.recurrence_interval) : null,
    recurrence_count: recurrenceType === 'count' ? number(values.recurrence_count) : null,
    recurrence_until_date:
      recurrenceType === 'until_date' ? text(values.recurrence_until_date) || null : null,
  };
}

const DATE_TYPES: readonly ContractManagementDateType[] = Object.values(ContractManagementDateType);

/** A new date's form values as an API body: `dateBody` plus the type it is created as. */
export function dateCreateBody(values: Record<string, unknown>): ContractManagementDateCreate {
  const type = DATE_TYPES.find(t => t === values.type);
  if (!type) throw new Error('Pick a date type');
  return { ...dateBody(values), type };
}

const RECURRENCE_TYPES: readonly DateRecurrence['recurrence_type'][] = [
  'never',
  'count',
  'until_date',
  'infinite',
];

const RECURRENCE_FREQUENCIES: readonly NonNullable<DateRecurrence['recurrence_frequency']>[] = [
  'day',
  'week',
  'month',
  'quarter',
  'year',
];

const positive = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;

/**
 * The recurrence a stored calendar event carries (extraction output, a human
 * overwrite, or a half-filled editor draft). Older values predate the required
 * `recurrence_type`, and some mark a one-off date with a `'never'` frequency;
 * both read as not repeating. Parts the recurrence type doesn't use come back
 * null.
 */
export function readRecurrence(event: Record<string, unknown>): DateRecurrence {
  const recurrenceType =
    event.recurrence_frequency === 'never'
      ? 'never'
      : (RECURRENCE_TYPES.find(t => t === event.recurrence_type) ?? 'never');
  const repeats = recurrenceType !== 'never';
  return {
    recurrence_type: recurrenceType,
    recurrence_frequency: repeats
      ? (RECURRENCE_FREQUENCIES.find(f => f === event.recurrence_frequency) ?? null)
      : null,
    recurrence_interval: repeats ? positive(event.recurrence_interval) : null,
    recurrence_count: recurrenceType === 'count' ? positive(event.recurrence_count) : null,
    recurrence_until_date:
      recurrenceType === 'until_date' ? text(event.recurrence_until_date) || null : null,
  };
}

/** A stored calendar event as a full `CalendarEvent`, or null when it has no event date. */
export function parseCalendarEvent(value: unknown): CalendarEvent | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  const event = value as Record<string, unknown>;
  const eventDate = text(event.event_date);
  if (!eventDate) return null;
  return { title: text(event.title) || null, event_date: eventDate, ...readRecurrence(event) };
}
